import { useEffect, useMemo, useState } from "react";
import {
  Download, Filter, RefreshCw, Timer, Trash2, Check, X, Settings2, FileDown,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";
import { api, apiErrorMessage, getToken, API, formatTanggalID, formatRupiah } from "@/lib/api";

const ANY = "__ANY__";
const STATUS_OPTIONS = ["Menunggu", "Disetujui", "Ditolak"];

function toDateInput(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function AdminLaporanLembur() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(toDateInput(first));
  const [endDate, setEndDate] = useState(toDateInput(today));
  const [unitId, setUnitId] = useState(ANY);
  const [lokasiId, setLokasiId] = useState(ANY);
  const [status, setStatus] = useState(ANY);
  const [units, setUnits] = useState([]);
  const [lokasi, setLokasi] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // 'xlsx' | 'csv' | null
  const [kuota, setKuota] = useState("30");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [u, l, s] = await Promise.all([
          api.get("/master/unit"),
          api.get("/master/lokasi"),
          api.get("/settings/overtime"),
        ]);
        setUnits(u.data);
        setLokasi(l.data);
        setKuota(String(s.data.kuota_bulanan_jam));
      } catch (e) {
        toast.error(apiErrorMessage(e));
      }
    })();
  }, []);

  const params = useMemo(
    () => ({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      unit_id: unitId !== ANY ? unitId : undefined,
      lokasi_id: lokasiId !== ANY ? lokasiId : undefined,
      status: status !== ANY ? status : undefined,
    }),
    [startDate, endDate, unitId, lokasiId, status]
  );

  async function loadReport() {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/overtime", { params });
      setRows(data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function refreshBadge() {
    window.dispatchEvent(new Event("atlas:refresh-approvals"));
  }

  async function doExport(kind) {
    setExporting(kind);
    try {
      const search = new URLSearchParams();
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") search.append(k, v);
      });
      const ext = kind === "csv" ? "export.csv" : "export";
      const url = `${API}/reports/overtime/${ext}?${search.toString()}`;
      const token = getToken();
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "Gagal export");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `laporan-lembur-${startDate}-${endDate}.${kind === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("Laporan berhasil diunduh");
    } catch (e) {
      toast.error(e.message || "Gagal export");
    } finally {
      setExporting(null);
    }
  }

  async function approveRow(r) {
    if (!window.confirm(`Setujui lembur ${r.nama} pada ${formatTanggalID(r.tanggal)}?`)) return;
    setWorking(true);
    try {
      await api.post(`/overtime/${r.id}/approve`);
      toast.success("Disetujui");
      await loadReport();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function submitReject() {
    if (rejectReason.trim().length < 3) {
      toast.error("Alasan penolakan wajib diisi (min. 3 karakter)");
      return;
    }
    setWorking(true);
    try {
      await api.post(`/overtime/${rejectTarget.id}/reject`, { alasan: rejectReason.trim() });
      toast.success("Pengajuan ditolak");
      setRejectTarget(null);
      setRejectReason("");
      await loadReport();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setWorking(false);
    }
  }

  async function del(r) {
    if (!window.confirm(`Hapus pengajuan lembur ${r.nama} pada ${formatTanggalID(r.tanggal)}?`)) return;
    try {
      await api.delete(`/overtime/${r.id}`);
      toast.success("Dihapus");
      await loadReport();
      refreshBadge();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function saveKuota() {
    const v = parseFloat(kuota);
    if (!v || v <= 0 || v > 200) {
      toast.error("Kuota harus 0.1 – 200 jam");
      return;
    }
    try {
      await api.put("/settings/overtime", { kuota_bulanan_jam: v });
      toast.success("Kuota bulanan disimpan");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  }

  async function downloadSpl(r) {
    const token = getToken();
    const url = `${API}/overtime/${r.id}/perintah-lembur.pdf`;
    try {
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.detail || "Gagal mengunduh PDF");
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const nama = (r.nama || "pegawai").replace(/\s+/g, "_");
      a.download = `Surat-Perintah-Lembur_${nama}_${r.tanggal}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      toast.success("PDF Surat Perintah Lembur berhasil diunduh");
    } catch (e) {
      toast.error(e.message || "Gagal mengunduh PDF");
    }
  }

  const stat = useMemo(() => {
    const total = rows.length;
    const setuju = rows.filter((r) => r.status === "Disetujui").length;
    const tolak = rows.filter((r) => r.status === "Ditolak").length;
    const totalJam = rows.reduce((a, r) => a + (r.actual_jam || 0), 0);
    return { total, setuju, tolak, totalJam: Math.round(totalJam * 100) / 100 };
  }, [rows]);

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto" data-testid="page-laporan-lembur">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-teal-700">
            Laporan Administrasi
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 mt-1 leading-tight">
            Rekap Lembur
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Semua pengajuan lembur lintas unit & lokasi. Waktu ditampilkan sesuai timezone Lokasi Kerja."
              : "Pengajuan lembur staf pada Unit Kerja Anda — unit lain otomatis tersembunyi."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={loadReport} disabled={loading} data-testid="lembur-refresh-btn" className="gap-2">
            <RefreshCw size={14} /> Muat Ulang
          </Button>
          {isAdmin ? (
            <Button
              onClick={() => doExport("xlsx")}
              disabled={exporting !== null || rows.length === 0}
              className="gap-2 bg-teal-800 hover:bg-teal-900"
              data-testid="lembur-export-xlsx-btn"
            >
              <Download size={14} /> {exporting === "xlsx" ? "Menyiapkan…" : "Export Excel"}
            </Button>
          ) : null}
        </div>
      </div>

      {/* Filter + Settings */}
      <div className={`mt-6 grid grid-cols-1 gap-4 ${isAdmin ? "lg:grid-cols-3" : ""}`}>
        <div className={`rounded-xl border border-slate-200 bg-white p-5 ${isAdmin ? "lg:col-span-2" : ""}`}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
            <Filter size={12} /> Filter Periode
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Dari</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5" data-testid="lembur-start-date" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Sampai</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5" data-testid="lembur-end-date" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Unit</Label>
              {isAdmin ? (
                <Select value={unitId} onValueChange={setUnitId}>
                  <SelectTrigger className="mt-1.5" data-testid="lembur-filter-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ANY}>Semua Unit</SelectItem>
                    {units.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>))}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className="mt-1.5 h-10 flex items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm text-slate-600"
                  data-testid="lembur-filter-unit-locked"
                >
                  {units.find((u) => u.id === user?.unit_id)?.nama || "Unit Kerja Anda"}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Lokasi</Label>
              <Select value={lokasiId} onValueChange={setLokasiId}>
                <SelectTrigger className="mt-1.5" data-testid="lembur-filter-lokasi"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Semua Lokasi</SelectItem>
                  {lokasi.map((l) => (<SelectItem key={l.id} value={l.id}>{l.nama}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5" data-testid="lembur-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>Semua Status</SelectItem>
                  {STATUS_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={loadReport} className="bg-teal-700 hover:bg-teal-800" data-testid="lembur-apply-filter-btn">
              Terapkan Filter
            </Button>
          </div>
        </div>
        {isAdmin ? (
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-slate-500 mb-4">
              <Settings2 size={12} /> Kuota Bulanan
            </div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Batas Jam Aktual / Bulan
            </Label>
            <div className="flex gap-2 mt-1.5">
              <Input
                type="number"
                step="0.5"
                min="0.5"
                max="200"
                value={kuota}
                onChange={(e) => setKuota(e.target.value)}
                data-testid="input-kuota-lembur"
              />
              <Button onClick={saveKuota} className="bg-teal-700 hover:bg-teal-800" data-testid="save-kuota-btn">
                Simpan
              </Button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">
              Kuota berlaku atas total jam <b>aktual</b> yang tercatat per pegawai per bulan.
            </p>
          </div>
        ) : null}
      </div>

      {/* Stats */}
      <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatBox label="Total Pengajuan" value={stat.total} tone="slate" testId="stat-ot-total" />
        <StatBox label="Disetujui" value={stat.setuju} tone="emerald" testId="stat-ot-setuju" />
        <StatBox label="Ditolak" value={stat.tolak} tone="rose" testId="stat-ot-tolak" />
        <StatBox label="Total Jam Aktual" value={stat.totalJam} tone="amber" testId="stat-ot-jam" />
      </div>

      {/* Table */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="sticky top-0">
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Tanggal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Pegawai</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Unit / Lokasi</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Rencana</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Aktual</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Jam</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Nilai Nominal</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Rincian Perhitungan</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500">Status</TableHead>
                <TableHead className="uppercase text-[10px] tracking-widest font-bold text-slate-500 text-right">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-500">Memuat…</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="text-center py-14">
                    <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
                      <Timer size={22} />
                    </div>
                    <div className="mt-3 font-semibold text-slate-700">Belum ada data untuk filter ini</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id} data-testid={`lembur-row-${r.id}`} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <TableCell className="font-semibold text-slate-800 whitespace-nowrap">
                      {r.tanggal}
                      <div className="text-[10px] text-slate-400 font-normal">{r.timezone}</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-slate-900">{r.nama}</div>
                      <div className="text-xs text-slate-500 font-mono-clock">{r.npp}</div>
                    </TableCell>
                    <TableCell className="text-slate-600 text-xs">
                      <div>{r.unit_nama}</div>
                      <div className="text-slate-400">{r.lokasi_nama}</div>
                    </TableCell>
                    <TableCell className="font-mono-clock text-slate-900 whitespace-nowrap">
                      {r.rencana_mulai} → {r.rencana_selesai}
                      <div className="text-[10px] text-slate-400 font-normal">{r.rencana_jam} jam</div>
                    </TableCell>
                    <TableCell className="font-mono-clock text-slate-900 whitespace-nowrap">
                      {r.actual_mulai_lokal || "—"} → {r.actual_selesai_lokal || "—"}
                    </TableCell>
                    <TableCell className="font-mono-clock text-teal-800 font-bold whitespace-nowrap">
                      {r.actual_jam != null ? `${r.actual_jam}h` : "—"}
                    </TableCell>
                    <TableCell data-testid={`lembur-nominal-${r.id}`} className="whitespace-nowrap">
                      {r.rincian_perhitungan ? (
                        <div className="font-mono-clock font-bold text-teal-800 text-sm">
                          {formatRupiah(r.rincian_perhitungan.nominal_total)}
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell data-testid={`lembur-rincian-${r.id}`}>
                      {r.rincian_perhitungan ? (
                        <div className="min-w-[240px] space-y-1">
                          <span
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                              r.rincian_perhitungan.is_libur
                                ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200"
                                : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                            }`}
                            data-testid={`lembur-jenis-hari-${r.id}`}
                          >
                            {r.rincian_perhitungan.jenis_hari}
                            {r.rincian_perhitungan.is_libur
                              ? ` · ${r.rincian_perhitungan.sistem_hari_kerja} HK`
                              : ""}
                          </span>
                          <div className="text-[10px] text-slate-600 leading-snug font-medium">
                            {r.rincian_perhitungan.formula}
                          </div>
                          {r.rincian_perhitungan.tiers?.length ? (
                            <div className="text-[10px] text-slate-500 leading-snug">
                              {r.rincian_perhitungan.tiers.map((t, i) => (
                                <span key={i}>
                                  {i > 0 ? " + " : ""}
                                  {t.jam}j×{t.pengali}× = {formatRupiah(t.nominal)}
                                </span>
                              ))}
                            </div>
                          ) : null}
                          <div className="text-[10px] text-slate-400">
                            Upah/jam: <b>{formatRupiah(r.rincian_perhitungan.upah_sejam)}</b>
                            {r.rincian_perhitungan.istirahat_jam > 0 ? (
                              <> · Istirahat −{r.rincian_perhitungan.istirahat_jam}j (efektif {r.rincian_perhitungan.jam_efektif}j)</>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <span className="text-[11px] text-slate-300">—</span>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={r.status} size="sm" /></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {!isAdmin ? (
                        <span className="text-[11px] text-slate-400 italic">Lihat saja</span>
                      ) : (
                        <>
                          {r.status === "Menunggu" ? (
                            <>
                              <button
                                onClick={() => approveRow(r)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900 mr-3"
                                data-testid={`lembur-approve-${r.id}`}
                              >
                                <Check size={13} /> Setujui
                              </button>
                              <button
                                onClick={() => { setRejectTarget(r); setRejectReason(""); }}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 hover:text-amber-900 mr-3"
                                data-testid={`lembur-reject-${r.id}`}
                              >
                                <X size={13} /> Tolak
                              </button>
                            </>
                          ) : null}
                          <button
                            onClick={() => downloadSpl(r)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900"
                            data-testid={`lembur-spl-pdf-${r.id}`}
                            title="Download Surat Perintah Lembur (PDF)"
                          >
                            <FileDown size={13} /> SPL
                          </button>
                          {/* Tombol Hapus dihilangkan (Keamanan Data — Feb 2026). */}
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent className="max-w-md" data-testid="lembur-reject-dialog">
          <DialogHeader>
            <DialogTitle>Tolak Pengajuan Lembur</DialogTitle>
            <DialogDescription>
              {rejectTarget ? `${rejectTarget.nama} — ${formatTanggalID(rejectTarget.tanggal)}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Alasan Penolakan (wajib)
            </Label>
            <Textarea
              className="mt-1.5"
              rows={3}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              data-testid="input-lembur-reject-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)}>Batal</Button>
            <Button onClick={submitReject} disabled={working} className="bg-rose-600 hover:bg-rose-700" data-testid="lembur-reject-confirm-btn">
              {working ? "Memproses…" : "Tolak Pengajuan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatBox({ label, value, tone, testId }) {
  const toneMap = {
    slate: "border-slate-200 text-slate-900",
    emerald: "border-emerald-200 text-emerald-800",
    rose: "border-rose-200 text-rose-800",
    amber: "border-amber-200 text-amber-800",
  };
  return (
    <div className={`rounded-xl border bg-white p-5 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">
        {label}
      </div>
      <div className="mt-1 font-mono-clock font-bold text-3xl leading-tight">{value}</div>
    </div>
  );
}
